import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaSave,
  FaSpinner,
  FaTag,
  FaDollarSign,
  FaRuler,
  FaFileAlt,
  FaListAlt,
} from 'react-icons/fa';
import { createCostItem, updateCostItem, getCostItemById } from './costItemsService';
import DashboardLayout from '../../layouts/DashboardLayout';
import Input from '../../components/shared/Input';
import TextArea from '../../components/shared/TextArea';
import MaskedInput from '../../components/shared/MaskedInput';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import FeedbackModal from '../../components/shared/ModalNotification';
import {
  FormContainer,
  FormSection,
  FormHeader,
  BackButton,
  FormGrid,
  FormField,
  SubmitButton,
} from '../../components/styled/FormComponents';

const CATEGORIES = ['Products', 'Materials', 'Crew Hours', 'Maintenance', 'Tools', 'Fuel', 'Others'];

const UNIT_SUGGESTIONS = ['hour', 'mile', 'gallon', 'sqft', 'each', 'ton', 'day'];

export default function CostItemForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!!id);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ open: false, message: '', success: true });

  const [item, setItem] = useState({
    name: '',
    category: '',
    unit: '',
    default_unit_price: '',
    description: '',
    isActive: true,
  });

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        const data = await getCostItemById(id);
        setItem({
          name: data.name || '',
          category: data.category || '',
          unit: data.unit || '',
          default_unit_price: data.default_unit_price != null ? String(data.default_unit_price) : '',
          description: data.description || '',
          isActive: data.isActive !== false,
        });
      } catch (error) {
        console.error('Error loading cost item:', error);
        setFeedback({ open: true, message: 'Failed to load cost item.', success: false });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!item.name.trim() || !item.category) {
      setFeedback({ open: true, message: 'Name and category are required.', success: false });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: item.name.trim(),
        category: item.category,
        unit: item.unit || null,
        default_unit_price: parseFloat(item.default_unit_price) || 0,
        description: item.description || null,
        isActive: item.isActive,
      };
      if (id) {
        await updateCostItem(id, payload);
      } else {
        await createCostItem(payload);
      }
      navigate('/cost-items');
    } catch (error) {
      const msg = error?.response?.data?.message || error.message || 'Failed to save cost item.';
      console.error('Error saving cost item:', error);
      setFeedback({ open: true, message: msg, success: false });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <FormContainer>
          <LoadingSpinner />
        </FormContainer>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <FormContainer>
        <FormHeader>
          <BackButton onClick={() => navigate('/cost-items')}>
            <FaArrowLeft /> Back to Cost Items
          </BackButton>
          <h2>{id ? 'Edit Cost Item' : 'New Cost Item'}</h2>
        </FormHeader>

        <form onSubmit={handleSubmit}>
          <FormSection>
            <h3>Item</h3>
            <FormGrid>
              <FormField index={0}>
                <Input
                  label="Name"
                  icon={<FaTag />}
                  value={item.name}
                  onChange={(e) => setItem({ ...item, name: e.target.value })}
                  placeholder="e.g. Labor Andre, Gray Truck Fuel"
                  required
                />
              </FormField>

              <FormField index={1}>
                <label
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    fontSize: 12,
                    color: '#555',
                    fontWeight: 600,
                  }}
                >
                  <span style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FaListAlt /> Category
                  </span>
                  <select
                    value={item.category}
                    onChange={(e) => setItem({ ...item, category: e.target.value })}
                    required
                    style={{
                      padding: '10px 12px',
                      borderRadius: 6,
                      border: '1px solid #ccc',
                      fontSize: 14,
                      background: '#fff',
                    }}
                  >
                    <option value="">Select category</option>
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </label>
              </FormField>
            </FormGrid>

            <FormGrid>
              <FormField index={2}>
                <Input
                  label="Unit"
                  icon={<FaRuler />}
                  list="unit-suggestions"
                  value={item.unit}
                  onChange={(e) => setItem({ ...item, unit: e.target.value })}
                  placeholder="hour, mile, gallon, sqft…"
                />
                <datalist id="unit-suggestions">
                  {UNIT_SUGGESTIONS.map((u) => <option key={u} value={u} />)}
                </datalist>
              </FormField>

              <FormField index={3}>
                <MaskedInput
                  label="Default Unit Price"
                  icon={<FaDollarSign />}
                  mask="currency"
                  value={item.default_unit_price}
                  onChange={(e) => setItem({ ...item, default_unit_price: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </FormField>
            </FormGrid>
          </FormSection>

          <FormSection>
            <h3>Notes</h3>
            <FormField index={4}>
              <TextArea
                label="Description (optional)"
                icon={<FaFileAlt />}
                value={item.description}
                onChange={(e) => setItem({ ...item, description: e.target.value })}
                placeholder="Internal notes shown next to the item in the picker"
                rows={3}
              />
            </FormField>

            {id && (
              <FormField index={5}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 14,
                    marginTop: 8,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={item.isActive}
                    onChange={(e) => setItem({ ...item, isActive: e.target.checked })}
                  />
                  Active (uncheck to archive — it will not show in the job cost picker)
                </label>
              </FormField>
            )}
          </FormSection>

          <SubmitButton type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <FaSpinner className="icon-spin" />
                {id ? 'Updating…' : 'Creating…'}
              </>
            ) : (
              <>
                <FaSave />
                {id ? 'Update Cost Item' : 'Create Cost Item'}
              </>
            )}
          </SubmitButton>
        </form>

        <FeedbackModal
          isOpen={feedback.open}
          message={feedback.message}
          success={feedback.success}
          onComplete={() => setFeedback((f) => ({ ...f, open: false }))}
        />
      </FormContainer>
    </DashboardLayout>
  );
}
